type PollOptions<A> = {
  /** predicate to check against the result of calling `fn` */
  untill: (res: {ok?: A; err?: Error}) => boolean

  /** duration in milliseconds to wait between polls  */
  interval: number

  /** will fail the call when the current timeout is exceeded */
  timeout?: number

  /** will fail the call when `fn` is attempted after `maxAttempts` limit is reached */
  maxAttempts?: number

  /** if true, the latest value of `fn` will be returned */
  dontThrow?: boolean
}

type PollResult<A> =
  | {
      type: "success"
      data: A
    }
  | {
      type: "pollLimit"
      /** possible data if a `pollFn` was successful */
      data?: A
      error: PollError | Error
    }

const defaults: Partial<PollOptions<unknown>> = {
  timeout: 10000,
  maxAttempts: 10
}

/**
 * Call a given `fn` repeatedly until a certain condition is met whilst also allowing for additional options like a timeout
 * @throws {Error}: if `opts.predicate` is not satisfied
 */
export async function poll<A>(pollFn: () => Promise<A>, opts: PollOptions<A>): Promise<PollResult<A>> {
  let pollCount = 0
  let data: A | undefined
  let error: Error | undefined
  const time = opts.timeout ?? defaults.timeout

  // start our timeout to fail the polling
  const failTimeout = createTimout(opts.timeout ?? defaults.timeout!)

  if (!opts.maxAttempts && !opts.timeout) throw new PollError("poll conditions not met")

  do {
    // Wait for opts.interval before running our pollFn again
    {
      // if this is the first loop we skip the wait, or if the interval is 0 there's no need to wait
      if (pollCount !== 0 && opts.interval !== 0) {
        const response = await Promise.race([
          failTimeout, //
          new Promise((res) => setTimeout(res, opts.interval))
        ])

        if (response instanceof PollError) {
          return {type: "pollLimit", data, error: error ?? response}
        }
      }
    }

    // increment the counter and fail if too many retries
    {
      pollCount++
      if (opts.maxAttempts && pollCount > opts.maxAttempts) {
        return {type: "pollLimit", data, error: error ?? new PollError(`exceeded limit: ${opts.maxAttempts}`)}
      }
    }

    // finally run our pollFn but fail early if our timeout is reached
    {
      const response = await Promise.race([failTimeout, pollFn().catch((e: Error) => e)])

      if (response instanceof PollError) {
        return {type: "pollLimit", data, error: response}
      } else if (response instanceof Error) {
        error = response
      } else {
        data = response
      }
    }
  } while (!opts.untill({ok: data, err: error}))

  if (!data) throw new PollError("Impossible case, should have gotten data")

  return {type: "success", data}
}

export class PollError extends Error {}

// don't be tempted to use promises.setTimout, it doesn't work with sinon at this time
function createTimout(time: number) {
  return new Promise<PollError>((res) => {
    setTimeout(() => res(new PollError(`timeout reached ${time}`)), time)
  })
}
